import { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cryptoClientCommandSchema, type CryptoClientCommand } from '../../schema/cryptoDashboard';
import { useCryptoWalletCommand, useCryptoWalletDetail } from '../../queries/cryptoDashboard';
import { copyToClipboard } from '../../utils';

const CryptoWalletDetails = ({
  marketplaceId,
  walletId,
  onClose,
  onViewAudit,
}: {
  marketplaceId: string;
  walletId: string;
  onClose: () => void;
  onViewAudit?: (clientId: string) => void;
}) => {
  const query = useCryptoWalletDetail(marketplaceId, walletId);
  const command = useCryptoWalletCommand(marketplaceId, walletId);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [copyError, setCopyError] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    getValues,
    trigger,
    setValue,
    formState: { errors },
  } = useForm<CryptoClientCommand>({
    resolver: zodResolver(cryptoClientCommandSchema),
    defaultValues: { action: 'disable', reason: '', confirmed: false },
  });
  const detail = query.data;
  const unresolved = detail?.operations.some(({ state }) => state !== 'confirmed');
  const walletNotReady = detail?.affectedWallets.some(({ state }) => state !== 'ready');
  const onSubmit = handleSubmit(async ({ action, reason }) => {
    if (!detail) return;
    try {
      await command.mutateAsync({
        command: 'availability',
        body: { action, reason, confirmed: true, requestId, walletFingerprint: detail.walletFingerprint },
      });
      setRequestId(crypto.randomUUID());
      setValue('confirmed', false);
    } catch {
      /* Inspect the persisted operation before attempting another command. */
    }
  });
  const refresh = async () => {
    const reason = getValues('reason').trim();
    if (!(await trigger('reason'))) return;
    try {
      await command.mutateAsync({ command: 'refresh', body: { reason } });
    } catch {
      /* Error and the persisted state are displayed below. */
    }
  };
  const copy = async (value: string) => {
    try {
      await copyToClipboard(value);
      setCopyError(false);
    } catch {
      setCopyError(true);
    }
  };
  return (
    <Paper component='section' aria-label='Static wallet details' variant='outlined' sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant='h6'>Static wallet details</Typography>
        <Button onClick={onClose} disabled={command.isPending}>
          Close wallet details
        </Button>
        <Button onClick={() => void query.refetch()} disabled={query.isFetching || command.isPending}>
          Reload stored details
        </Button>
        {query.isPending && <Typography>Loading wallet details…</Typography>}
        {query.isError && (
          <Alert severity='error'>
            Could not load wallet details. <Button onClick={() => void query.refetch()}>Retry details</Button>
          </Alert>
        )}
        {detail && (
          <>
            <Typography>
              Player: {detail.username ?? detail.playerId} · {detail.email ?? 'Email unavailable'}
            </Typography>
            <Link href={`/crm/players/${encodeURIComponent(detail.playerId)}/wallet/payment-transaction`}>
              Open player transactions
            </Link>
            <Typography>
              Merchant: {detail.merchantId} · Client: {detail.clientId}
            </Typography>
            <Typography>
              {detail.currency} · Stored state: {detail.state}
            </Typography>
            {detail.tag && (
              <Alert severity='warning'>
                Required destination tag: {detail.tag} <Button onClick={() => void copy(detail.tag!)}>Copy tag</Button>
              </Alert>
            )}
            <Typography sx={{ overflowWrap: 'anywhere' }}>Address: {detail.address ?? 'Not provisioned'}</Typography>
            {detail.address && <Button onClick={() => void copy(detail.address!)}>Copy address</Button>}
            {copyError && <Alert severity='error'>Could not copy. Copy the displayed value manually.</Alert>}
            <Typography>Provider observation: {detail.providerObservation?.status ?? 'Not checked'}</Typography>
            {detail.providerObservation && (
              <>
                <Typography>Checked: {detail.providerObservation.checkedAt}</Typography>
                <Typography>
                  Provider wallet balance: {detail.providerObservation.balance ?? 'Unavailable'} (not player balance)
                </Typography>
                {detail.providerObservation.status !== 'matched' && (
                  <Alert severity='warning'>
                    Provider state is {detail.providerObservation.status}. Stored address is retained; no wallet is
                    recreated.
                  </Alert>
                )}
              </>
            )}
            {onViewAudit && (
              <Button onClick={() => onViewAudit(detail.clientId)} disabled={command.isPending}>
                View client audit
              </Button>
            )}
            <Typography variant='h6'>Client-wide scope ({detail.affectedWallets.length} wallets)</Typography>
            {detail.affectedWallets.map((wallet) => (
              <Typography key={wallet.id} sx={{ overflowWrap: 'anywhere' }}>
                {wallet.currency} · {wallet.state} · {wallet.address ?? 'No address'} · Tag: {wallet.tag ?? 'None'}
              </Typography>
            ))}
            <Alert severity='warning'>
              Enable/disable applies to every provider wallet for this client, including currencies outside this page.
              Existing addresses can still receive funds; settlement is not stopped. Provider wallet-list responses do
              not expose enabled state.
            </Alert>
            {unresolved && (
              <Alert severity='warning'>
                A previous command has an uncertain or incomplete outcome. New commands are blocked; reconcile with
                0xProcessing.
              </Alert>
            )}
            <Stack component='form' spacing={2} onSubmit={onSubmit}>
              <Controller
                name='action'
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label='Client-wide action' disabled={command.isPending || unresolved}>
                    <MenuItem value='disable'>Disable all client wallets</MenuItem>
                    <MenuItem value='enable'>Enable all client wallets</MenuItem>
                  </TextField>
                )}
              />
              <TextField
                label='Operator reason'
                {...register('reason')}
                error={!!errors.reason}
                helperText={errors.reason?.message}
                disabled={command.isPending}
              />
              <Controller
                name='confirmed'
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label='I confirm this affects all wallets of this client'
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                        disabled={command.isPending}
                      />
                    }
                  />
                )}
              />
              {errors.confirmed && <Alert severity='error'>{errors.confirmed.message}</Alert>}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <Button type='submit' variant='contained' disabled={command.isPending || unresolved || walletNotReady}>
                  Submit client-wide command
                </Button>
                <Button onClick={() => void refresh()} disabled={command.isPending}>
                  Refresh from provider (reason required)
                </Button>
              </Stack>
            </Stack>
            {command.isPending && <Typography>Submitting; awaiting a persisted result…</Typography>}
            {command.isError && (
              <Alert severity='error'>
                Command failed or its response was lost. Refresh details and inspect the operation before retrying.
              </Alert>
            )}
            {detail.operations.map((operation) => (
              <Alert key={operation.id} severity={operation.state === 'confirmed' ? 'info' : 'warning'}>
                {operation.action}:{' '}
                {operation.state === 'confirmed' ? 'Provider acknowledged command' : operation.state} · Operation:{' '}
                {operation.id} · Actor: {operation.actorId} · {operation.createdAt} · {operation.reason}
              </Alert>
            ))}
            <Typography variant='h6'>Recent payment references (up to {detail.paymentLimit})</Typography>
            {!detail.payments.length && <Typography>No captured payments for this wallet.</Typography>}
            {detail.payments.map((payment) => (
              <Typography key={payment.id}>
                Payment {payment.paymentId} · Callback: {payment.state} · Delivery:{' '}
                {payment.deliveryState ?? 'Not created'} · {payment.createdAt}
              </Typography>
            ))}
          </>
        )}
      </Stack>
    </Paper>
  );
};
export default CryptoWalletDetails;
